'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Check } from 'lucide-react';
import posthog from 'posthog-js';

type Variant = 'control' | 'start-free' | 'no-secondary';

const ctaText: Record<Variant, string> = {
  'control': 'Try it free',
  'start-free': 'Start for free',
  'no-secondary': 'Try it free',
};

const benefitChips = [
  'Free for small teams',
  '5-minute setup',
  'Slack, Figma & Claude',
];

export function HeroCTA() {
  const [variant, setVariant] = useState<Variant>('control');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Wait for PostHog to load, then get the variant
    const check = () => {
      if (posthog.__loaded) {
        const v = posthog.getFeatureFlag('hero-cta-experiment') as Variant;
        if (v) setVariant(v);
        setReady(true);
      }
    };

    check();
    // If not loaded yet, try again shortly
    if (!ready) {
      const timer = setTimeout(check, 500);
      return () => clearTimeout(timer);
    }
  }, [ready]);

  const trackClick = (cta: string, href: string) => {
    if (posthog.__loaded) {
      posthog.capture('marketing_cta_clicked', {
        cta,
        variant,
        location: 'hero',
        href,
      });
    }
  };

  const showSecondary = variant !== 'no-secondary';

  return (
    <>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
        <Link
          href="/auth"
          className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-all rounded-lg text-lg shadow-lg hover:shadow-xl group"
          onClick={() => trackClick('hero_primary', '/auth')}
        >
          {ctaText[variant]}
          <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
        </Link>
        {showSecondary && (
          <Link
            href="/demo/feed"
            className="inline-flex items-center justify-center px-6 py-3.5 border border-gray-300 text-gray-700 font-semibold hover:border-gray-400 hover:text-gray-900 transition-all rounded-lg"
            onClick={() => trackClick('hero_see_demo', '/demo/feed')}
          >
            Try the live demo · No signup
          </Link>
        )}
      </div>
      <ul className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-600">
        {benefitChips.map((chip) => (
          <li key={chip} className="flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" aria-hidden="true" />
            {chip}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-gray-400">
        No credit card required.
      </p>
    </>
  );
}
